import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import map from "lodash/map";
import throttle from "lodash/throttle";
import { useIntl } from "react-intl";

import Subcategory from "../subcategory/Subcategory";
import Collapsible from "../../components/collapsible/Collapsible";
import { GlobalContext } from "../../globals/globalContext";
import "./styles.styl";

export default function Category({
  name: parentName,
  products = [],
  onChange,
  sold,
  editable,
  allow_ordering_before_suggestions_at,
  handleClick,
  openedId,
  items_deliver_listings,
  delivery,
  reRender,
  categoryRef,
  isGrid,
  ownerlessShop,
  setOpenedSubCategory,
  listRef,
  categoryWrapperRef,
}) {
  const intl = useIntl();
  const otherTranslated = intl.formatMessage({ id: "other" });

  parentName = parentName === "undefined" ? otherTranslated : parentName;

  const [visibleItemId, setVisibleItemId] = useState(null);
  const { activeSearch, setActiveSearch, isMobile } = useContext(GlobalContext);
  const subcategoryRefs = useRef({});
  const scrollableContainerRef = isMobile ? categoryRef : categoryWrapperRef;
  const [showScrollTop, setShowScrollTop] = useState(false);

  const determineVisibleSubcategory = useCallback(() => {
    if (!scrollableContainerRef?.current) {
      return null;
    }

    const subcategoryEntries = Object.entries(subcategoryRefs.current);
    const containerRect =
      scrollableContainerRef.current.getBoundingClientRect();

    const visibleSubcategory = subcategoryEntries.reduce(
      (mostVisible, [name, ref]) => {
        if (!ref) return mostVisible;

        const refRect = ref.getBoundingClientRect();

        // Check if element is at the top of the container within 10px margin
        const isAtTop =
          refRect.top <= containerRect.top + 10 &&
          refRect.bottom > containerRect.top;

        if (isAtTop) {
          return {
            name,
            top: refRect.top,
          };
        }
        return mostVisible;
      },
      null,
    );

    return visibleSubcategory ? visibleSubcategory.name : null;
  }, [scrollableContainerRef]);

  const handleScroll = useCallback(
    throttle(() => {
      const newVisibleItemId = determineVisibleSubcategory();

      if (scrollableContainerRef?.current) {
        // Show button when scrolled at least 10px
        setShowScrollTop(scrollableContainerRef.current.scrollTop > 10);
      }

      if (setOpenedSubCategory) {
        setOpenedSubCategory(newVisibleItemId);
      }
    }, 100),
    [determineVisibleSubcategory, visibleItemId, setOpenedSubCategory],
  );

  useEffect(() => {
    const currentListRef = scrollableContainerRef?.current;

    if (currentListRef) {
      currentListRef.addEventListener("scroll", handleScroll, {
        passive: true,
      });

      // Initial check to handle cases where the page loads in a scrolled state
      handleScroll();

      return () => {
        currentListRef.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, scrollableContainerRef]);

  // Sort and process products
  const ordered = Object.keys(products)
    .sort((a, b) => {
      if (a === "undefined") return -1;
      if (b === "undefined") return 1;
      return 1;
    })
    .reduce((obj, key) => {
      obj[key] = products[key];
      return obj;
    }, {});

  const subcategories = map(ordered, (products, name) => (
    <div
      key={name}
      data-name={name}
      ref={(el) => (subcategoryRefs.current[name] = el)}
    >
      <Subcategory
        name={name}
        categoryId={parentName}
        products={products}
        onChange={onChange}
        sold={sold}
        editable={editable}
        allow_ordering_before_suggestions_at={
          allow_ordering_before_suggestions_at
        }
        subcategoryCount={Object.keys(ordered).length}
        items_deliver_listings={items_deliver_listings}
        delivery={delivery}
        reRender={reRender}
        isGrid={isGrid}
        ownerlessShop={ownerlessShop}
        listRef={listRef}
        setVisibleItemId={setVisibleItemId}
        visibleItemId={visibleItemId}
      />
    </div>
  ));

  const scrollToTop = () => {
    if (scrollableContainerRef?.current) {
      scrollableContainerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <Collapsible
        key={parentName}
        label={parentName}
        children={subcategories}
        id={parentName}
        isOpen={openedId === parentName}
        handleClick={handleClick}
        categoryRef={categoryRef}
        activeSearch={activeSearch}
      />
      {showScrollTop && (
        <div className="catagory__scrollwrapper">
          <button onClick={scrollToTop} className="catagory__scrollTopbutton">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}
